/**
 * useVoiceRecorder — Voice Message Recording
 *
 * Flow: click mic → recording → click stop → preview (play/delete/send) → click send → upload
 */

import { useState, useRef, useCallback, useEffect } from "react";

export type VoiceState = "idle" | "recording" | "preview" | "uploading";

export const hasMediaRecorder = typeof window !== "undefined" && !!window.MediaRecorder;

export function useVoiceRecorder(channelId: string) {
  const [state, setState] = useState<VoiceState>("idle");
  const [recordingDuration, setRecordingDuration] = useState(0);
  /** Object URL for audio preview playback */
  const [previewUrl, setPreviewUrl] = useState<string | null>(null);

  // Refs
  const mediaRecorderRef = useRef<MediaRecorder | null>(null);
  const mediaStreamRef = useRef<MediaStream | null>(null);
  const chunksRef = useRef<Blob[]>([]);
  const timerRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const startTimeRef = useRef<number>(0);
  /** Stored blob after recording stops (for upload on send) */
  const blobRef = useRef<Blob | null>(null);
  const mimeRef = useRef<string>("audio/webm");

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      cancelRecording();
    };
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // ── Start Recording ──

  const startRecording = useCallback(async () => {
    if (!hasMediaRecorder) return;
    if (state !== "idle") return;

    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      mediaStreamRef.current = stream;

      const mimeType = MediaRecorder.isTypeSupported("audio/webm;codecs=opus")
        ? "audio/webm;codecs=opus"
        : MediaRecorder.isTypeSupported("audio/webm")
          ? "audio/webm"
          : MediaRecorder.isTypeSupported("audio/mp4")
            ? "audio/mp4"
            : "";

      const recorder = new MediaRecorder(stream, mimeType ? { mimeType } : undefined);
      mediaRecorderRef.current = recorder;
      chunksRef.current = [];

      recorder.ondataavailable = (e) => {
        if (e.data.size > 0) {
          chunksRef.current.push(e.data);
        }
      };

      recorder.start(100);
      startTimeRef.current = Date.now();
      setRecordingDuration(0);
      setState("recording");

      timerRef.current = setInterval(() => {
        const elapsed = Math.floor((Date.now() - startTimeRef.current) / 1000);
        setRecordingDuration(elapsed);
      }, 500);
    } catch (err) {
      console.error("Failed to start recording:", err);
    }
  }, [state]);

  // ── Stop Recording → Preview ──

  const stopRecording = useCallback((): Promise<void> => {
    return new Promise((resolve) => {
      const recorder = mediaRecorderRef.current;
      if (!recorder || recorder.state === "inactive") {
        resolve();
        return;
      }

      if (timerRef.current) {
        clearInterval(timerRef.current);
        timerRef.current = null;
      }

      recorder.onstop = () => {
        mediaStreamRef.current?.getTracks().forEach((t) => t.stop());
        mediaStreamRef.current = null;

        const actualMime = recorder.mimeType || "audio/webm";
        const blob = new Blob(chunksRef.current, { type: actualMime });
        chunksRef.current = [];

        if (blob.size < 1000) {
          setState("idle");
          resolve();
          return;
        }

        blobRef.current = blob;
        mimeRef.current = actualMime;

        // Create preview URL for playback
        const url = URL.createObjectURL(blob);
        setPreviewUrl(url);
        setState("preview");
        resolve();
      };

      recorder.stop();
    });
  }, []);

  // ── Send (upload) the recorded voice message ──

  const sendRecording = useCallback(async (): Promise<string | null> => {
    const blob = blobRef.current;
    if (!blob) return null;

    setState("uploading");

    try {
      const ext = mimeRef.current.includes("mp4") ? "m4a" : "webm";
      const filename = `voice-${Date.now()}.${ext}`;

      // Verschlüsselt hochladen wie jeder andere Anhang — Begründung und
      // Vorgeschichte stehen in lib/voice-upload.ts.
      const { ladeSprachnachrichtHoch } = await import("../lib/voice-upload");
      const attachment = await ladeSprachnachrichtHoch({
        blob,
        mimeType: mimeRef.current,
        filename,
        channelId,
      });

      // Cleanup
      if (previewUrl) URL.revokeObjectURL(previewUrl);
      setPreviewUrl(null);
      blobRef.current = null;
      setState("idle");
      setRecordingDuration(0);
      return attachment;
    } catch (err) {
      console.error("Voice upload failed:", err);
      setState("preview"); // Back to preview so user can retry
      return null;
    }
  }, [previewUrl]);

  // ── Cancel / Discard ──

  const cancelRecording = useCallback(() => {
    if (timerRef.current) {
      clearInterval(timerRef.current);
      timerRef.current = null;
    }

    const recorder = mediaRecorderRef.current;
    if (recorder && recorder.state !== "inactive") {
      recorder.onstop = null;
      recorder.stop();
    }
    mediaRecorderRef.current = null;

    mediaStreamRef.current?.getTracks().forEach((t) => t.stop());
    mediaStreamRef.current = null;

    chunksRef.current = [];
    blobRef.current = null;

    if (previewUrl) URL.revokeObjectURL(previewUrl);
    setPreviewUrl(null);
    setRecordingDuration(0);
    setState("idle");
  }, [previewUrl]);

  return {
    state,
    recordingDuration,
    previewUrl,
    startRecording,
    stopRecording,
    sendRecording,
    cancelRecording,
  };
}
